import { createMutation } from '../mutation/createMutation';
import { KeyNamespaces } from '../utils/getKey';
import type { UpdateByIdArg, UseUpdateEntity } from './createPaginationEntityTypes';
import type { CacheKey } from '../utils/internalTypings';
import type { MutationImperativeHelpersReturn } from '../mutation/createMutationTypes';
import { getQueryClient } from '../queryClientSingleton';
import { updateEntityCache, updateEntityListCache } from './createPaginationEntityCacheHelpers';

function getMutationUpdateFn<Entity, Payload>(
    {
        fetch,
        name,
        selectId,
    }: {
        fetch: (payload: Payload) => Promise<Entity>;
        name: string;
        selectId: CacheKey<Entity>;
    },
    client = getQueryClient(),
) {
    return async (payload: Payload) => {
        const entity = await fetch(payload);

        updateEntityCache(
            entity,
            selectId(entity),
            {
                name,
            },
            client,
        );

        updateEntityListCache(
            [entity],
            {
                name,
                selectId,
            },
            client,
        );

        return entity;
    };
}

export const createPaginationEntityMutationUpdate = <
    Entity,
    UpdatePayload extends UpdateByIdArg<Entity> = UpdateByIdArg<any>,
>({
    updateById,
    name,
    selectId,
}: {
    name: string;
    updateById?: (payload: UpdatePayload) => Promise<Entity>;
    selectId: CacheKey<Entity>;
}): [
    UseUpdateEntity<Entity, UpdatePayload>,
    MutationImperativeHelpersReturn<Entity, UpdatePayload>,
] => {
    const updateFetch = getMutationUpdateFn<Entity, UpdatePayload>({
        name,
        fetch: (updatePayload: UpdatePayload) => {
            if (!updateById) {
                throw new Error('createPaginationEntity: updateById is not defined');
            }
            return updateById(updatePayload);
        },
        selectId: selectId as unknown as CacheKey<Entity>,
    });
    const [useUpdateMutation, updateMutationEntityHelpers] = createMutation({
        name: `${KeyNamespaces.PAGINATION_ENTITY}/${name}/detail/update`,
        cache: false,
        payloadCreator: updateFetch,
    });
    const useUpdateMutationWrapper: UseUpdateEntity<Entity, UpdatePayload> = (arg) => {
        const result = useUpdateMutation(arg as UpdatePayload);
        return {
            data: result.data,
            updateById: result.mutate,
            error: result.error,
            mutate: result.mutate,
            reset: result.reset,
            isPending: result.isPending,
        };
    };

    return [useUpdateMutationWrapper, updateMutationEntityHelpers];
};
